import { PATCH_MM } from "../sim/collision";

/** `SHADOWMAP_XSIZE` (`PRTerrainLib/Terrain.h:39`): texels per patch edge. */
const SHADOWMAP_SIZE = 256;
/** `TERRAIN_XSIZE` (`Terrain.h:53-58`), in millimetres: one patch. */
const TERRAIN_SIZE_MM = PATCH_MM;

/** A map's `terrain_shade.png` as the page decoded it: RGBA bytes, one texel
 *  per world metre, patch (0, 0) first. */
export interface ShadeRaster {
  readonly rgba: Uint8ClampedArray;
  readonly width: number;
  readonly height: number;
  readonly patchesX: number;
  readonly patchesY: number;
}

/** Full colour: the shade off the terrain, and of an actor no shade raster
 *  has reached. */
export const FULL_SHADE: readonly [number, number, number] = [1, 1, 1];
const FULL: [number, number, number] = [1, 1, 1];

/** `CMapOutdoor::GetShadowMapColor` (`MapOutdoor.cpp:1097-1199`) and
 *  `CTerrainImpl::GetShadowMapColor` (`Terrain.cpp:303-323`): the colour a
 *  character standing here is lit by, as a fraction of full colour per channel.
 *  y is taken as its magnitude, the patch-local position is scaled by
 *  `SHADOWMAP_XSIZE - 1` and truncated, and a point off the terrain is full
 *  colour. */
export function shadeAt(raster: ShadeRaster, xMm: number, yMm: number): [number, number, number] {
  const y = Math.abs(yMm);
  if (xMm < 0) return FULL;
  const patchX = Math.trunc(xMm / TERRAIN_SIZE_MM);
  const patchY = Math.trunc(y / TERRAIN_SIZE_MM);
  if (patchX >= raster.patchesX || patchY >= raster.patchesY) return FULL;
  const localX = xMm - patchX * TERRAIN_SIZE_MM;
  const localY = y - patchY * TERRAIN_SIZE_MM;
  const texelX = Math.trunc((localX / TERRAIN_SIZE_MM) * (SHADOWMAP_SIZE - 1));
  const texelY = Math.trunc((localY / TERRAIN_SIZE_MM) * (SHADOWMAP_SIZE - 1));
  const col = patchX * SHADOWMAP_SIZE + texelX;
  const row = patchY * SHADOWMAP_SIZE + texelY;
  if (col >= raster.width || row >= raster.height) return FULL;
  const at = (row * raster.width + col) * 4;
  return [raster.rgba[at] / 255, raster.rgba[at + 1] / 255, raster.rgba[at + 2] / 255];
}

/** `terrain_shade.png`, decoded, as the raster `shadeAt` reads: one patch per
 *  `SHADOWMAP_XSIZE` texels on each axis. */
export function decodeShade(image: ImageBitmap | HTMLImageElement): ShadeRaster {
  const { width, height } = image;
  const scratch = document.createElement("canvas");
  scratch.width = width;
  scratch.height = height;
  const ctx = scratch.getContext("2d", { willReadFrequently: true });
  if (ctx === null) throw new Error("no Canvas2D context to decode the shade map with");
  ctx.drawImage(image, 0, 0);
  return {
    rgba: ctx.getImageData(0, 0, width, height).data,
    width,
    height,
    patchesX: Math.floor(width / SHADOWMAP_SIZE),
    patchesY: Math.floor(height / SHADOWMAP_SIZE),
  };
}
